import { isTypingTarget, } from "@vninvestcharts/engine-drawing";
import { useCallback, useEffect, useRef, useState } from "react";
export function useToolbarKeyboardNav(options) {
    const { onAction, onDeleteAll, onCloneAll, onGroupAll, selectedCount, actionIds } = options;
    const containerRef = useRef(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    // S0/BUG-5: NO autofocus here — the owner component (FloatingSelectionToolbar)
    // runs a single guarded autofocus (skips when the user is text-editing).
    const focusButton = useCallback((index) => {
        const el = containerRef.current?.querySelectorAll("[data-toolbar-btn]");
        if (!el || el.length === 0)
            return;
        const clamped = ((index % el.length) + el.length) % el.length;
        setFocusedIndex(clamped);
        el[clamped]?.focus();
    }, []);
    useEffect(() => {
        // Keep focusedIndex in bounds when actionIds change
        if (focusedIndex >= actionIds.length)
            setFocusedIndex(0);
    }, [actionIds.length, focusedIndex]);
    const handleKeyDown = useCallback((e) => {
        // Never steal keys while typing in an input
        if (isTypingTarget(e))
            return;
        const isMulti = selectedCount !== undefined && selectedCount > 1;
        // Delete / Backspace
        if (e.key === "Delete" || e.key === "Backspace") {
            if (e.ctrlKey || e.metaKey || e.altKey)
                return;
            e.preventDefault();
            if (isMulti)
                onDeleteAll?.();
            else
                onAction("delete");
            return;
        }
        // Ctrl/Cmd + D -> clone / duplicate
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
            e.preventDefault();
            if (isMulti) {
                if (onCloneAll)
                    onCloneAll();
                else
                    onAction("clone");
            }
            else
                onAction("clone");
            return;
        }
        // L -> toggleLock
        if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "l") {
            e.preventDefault();
            onAction("toggleLock");
            return;
        }
        // H -> toggleVisibility
        if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "h") {
            e.preventDefault();
            onAction("toggleVisibility");
            return;
        }
        // Ctrl/Cmd+G -> group (multi only)
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "g" && isMulti) {
            e.preventDefault();
            onGroupAll?.();
            return;
        }
        // Arrow navigation for roving tabindex
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
            e.preventDefault();
            const dir = e.key === "ArrowRight" ? 1 : -1;
            focusButton(focusedIndex + dir);
            return;
        }
        if (e.key === "Home") {
            e.preventDefault();
            focusButton(0);
            return;
        }
        if (e.key === "End") {
            e.preventDefault();
            focusButton(actionIds.length - 1);
            return;
        }
    }, [onAction, onDeleteAll, onCloneAll, onGroupAll, selectedCount, actionIds, focusedIndex, focusButton]);
    return { containerRef, focusedIndex, setFocusedIndex, handleKeyDown, focusButton };
}
